import { PrimaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import InviteEmailDesignApplyTemplateModal from 'features/InviteEmailDesign/InviteEmailDesignApplyTemplateModal'
import { INVITE_EMAIL_DESIGN_SCOPE_ROWS } from 'features/InviteEmailDesign/inviteEmailDesignScopeRows'
import { resolveInviteEmailDesignTemplateForScope } from 'features/InviteEmailDesign/inviteEmailDesignTemplateHelpers'
import { getInviteEmailTemplateVisibleScopeRows } from 'features/ManageCampaigns/utils/inviteCampaignGroupModes'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import { useEffect, useMemo, useState } from 'react'

import InviteEmailDesignManageLink from './InviteEmailDesignManageLink'
import styles from './InviteMessageScopeSettings.module.scss'

const InviteEmailTemplateSettings = ({
  templates = [],
  campaigns = [],
  contentSubsidiaryId,
  campaignGroupMode,
  mediumOverrides = {},
  isLoading = false,
  onTemplatesChange
}) => {
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()

  const [activeScopeRow, setActiveScopeRow] = useState(null)
  const [scopeTemplateOverrides, setScopeTemplateOverrides] = useState({})

  const visibleScopeRows = useMemo(
    () =>
      getInviteEmailTemplateVisibleScopeRows(campaignGroupMode, campaigns, mediumOverrides),
    [campaignGroupMode, campaigns, mediumOverrides]
  )

  useEffect(() => {
    if (!activeScopeRow) {
      return
    }

    const isStillVisible = visibleScopeRows.some(row => row.scope === activeScopeRow.scope)

    if (!isStillVisible) {
      setActiveScopeRow(null)
    }
  }, [activeScopeRow, visibleScopeRows])

  const displayedTemplatesByScope = useMemo(() => {
    return INVITE_EMAIL_DESIGN_SCOPE_ROWS.reduce((acc, row) => {
      const overrideId = scopeTemplateOverrides[row.scope]
      const overrideTemplate = overrideId
        ? templates.find(template => template.id === overrideId)
        : null

      acc[row.scope] =
        overrideTemplate || resolveInviteEmailDesignTemplateForScope(templates, row.scope)

      return acc
    }, {})
  }, [scopeTemplateOverrides, templates])

  function openApplyModal(scopeRow) {
    if (promptEmailSubscriptionUpgrade()) {
      return
    }

    setActiveScopeRow(scopeRow)
  }

  function closeApplyModal() {
    setActiveScopeRow(null)
  }

  function handleDesignApplied({ scope, templateId }) {
    setScopeTemplateOverrides(current => ({
      ...current,
      [scope]: templateId
    }))

    if (onTemplatesChange) {
      onTemplatesChange()
    }
  }

  function openDesignModalForScope(scope) {
    const scopeRow = INVITE_EMAIL_DESIGN_SCOPE_ROWS.find(row => row.scope === scope)

    if (!scopeRow) {
      return
    }

    openApplyModal(scopeRow)
  }

  function renderDesignRow(scope, title) {
    const template = displayedTemplatesByScope[scope]
    const isEditDisabled = isLoading || !templates.length

    return (
      <div key={scope} className={styles.designScopeRow}>
        <div className={styles.scopeLabelCell}>
          <span className={styles.scopeTitle}>{title}</span>
        </div>
        <div className={styles.designCell}>
          <span className={styles.designName} title={template?.name || undefined}>
            {template?.name || 'Not set'}
          </span>
          <PrimaryRegularButton
            className={styles.editDesignButton}
            disabled={isEditDisabled}
            onClick={() => openDesignModalForScope(scope)}
          >
            Change template
          </PrimaryRegularButton>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={styles.designToolsBar}>
        <div className={styles.designToolsCopy}>
          <p className={styles.designToolsTitle}>Email templates</p>
          <p className={styles.designToolsDescription}>
            Set up how your invite emails look, including logo, hero image, colors, and footer.
          </p>
        </div>
        <InviteEmailDesignManageLink />
      </div>

      {visibleScopeRows.length > 0 && (
        <div className={styles.settingsCard}>
          <div className={styles.settingsTable}>
            <div className={styles.designTableHeader}>
              <span>Campaign group</span>
              <span>Email template</span>
            </div>

            {visibleScopeRows.map(row => renderDesignRow(row.scope, row.title))}
          </div>
        </div>
      )}

      <InviteEmailDesignApplyTemplateModal
        isOpen={Boolean(activeScopeRow)}
        scopeRow={activeScopeRow}
        templates={templates}
        campaigns={campaigns}
        contentSubsidiaryId={contentSubsidiaryId}
        onClose={closeApplyModal}
        onApplied={handleDesignApplied}
      />
    </>
  )
}

export default InviteEmailTemplateSettings
